import React from 'react';

import { Flex, Box, GridItem, Grid } from '@chakra-ui/react';
import { OKROwnerAndCollaboratorComponent } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/OKROwnerAndCollaborator';
import { MeasureSelect } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Measure/OKRMeasure';
import { ParentOKR } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Overview/ParentOKR';
import { StyledInputMaterialUITextArea } from 'components/RabbitOKRSidePanel/SecondaryPanel/TitleAndMetricsSuggestionPanel/AdvancedOptionsScreen';
import { SECONDARY_PANEL_TYPES } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { TimelineSelect } from 'components/TimelineSelect/TimelineSelect';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';

import { OBJECTIVE_LEVEL, OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';

import { InputV2 } from 'uiToolkitV1/InputV2';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

function GradientCardV2({
  suggestedOKRDetails,
  onChange = () => {},
  showOwnersAndCollaborators = true,
  containerProps = {},
  allSuggestedOKRDetails = {},
  currentCardIndex,
  showTrackingSection = true,
  isAlignmentDisabled = false,
  okrDetails = {},
  isNewOKR = true,
}) {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { type, visibilityType, objectiveLevel, ifGoalChildExists } = okrDetails;
  const isObjective = type === OKR_TYPES.OBJECTIVE;

  const { canCreateKeyResultAtRootLevel } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const isParentOptional =
    type === OKR_TYPES.OBJECTIVE ? objectiveLevel === OBJECTIVE_LEVEL.COMPANY : canCreateKeyResultAtRootLevel;

  const isParentMandatory = !isParentOptional;

  const labelText = (
    <span>
      What{' '}
      {isObjective
        ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
        : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)}{' '}
      this {modifyString(moduleName, StringModifier.SENTENCE_CASE)} align to.
      {isParentMandatory ? <sup style={{ top: 0, fontSize: '14px' }}>*</sup> : ''}
    </span>
  );
  const alignmentGridTemplate = isMobileView()
    ? { gridTemplateRows: '44px 44px', rowGap: '16px' }
    : { gridTemplateColumns: '25% 75%', columnGap: '16px', h: '44px' };

  return (
    <Flex
      direction="column"
      borderRadius="8px"
      width="100%"
      padding="1px"
      bgGradient="linear(to-r, rgba(0, 142, 240, 0.2), rgba(236, 59, 100, 0.2), rgba(255, 194, 0, 0.2),rgba(92, 196, 97, 0.2))"
      boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)"
      {...containerProps}
    >
      <Box background="white" width="100%" borderRadius="7px">
        <Flex
          bgGradient="linear(to-r, rgba(0, 142, 240, 0.02), rgba(236, 59, 100, 0.02), rgba(255, 194, 0, 0.02), rgba(92, 196, 97, 0.02))"
          padding="12px 16px 24px 16px"
          flexDirection="column"
          alignItems="flex-start"
          gap="16px"
          alignSelf="stretch"
        >
          <StyledInputMaterialUITextArea
            value={suggestedOKRDetails?.title}
            onChange={(event) => onChange({ ...suggestedOKRDetails, title: event.target.value })}
          />

          {showTrackingSection && (
            <Box width="100%">
              <Grid
                gridTemplateColumns={isMobileView() ? '40% 10% 40%' : '15% 4% 19% 12% 42%'}
                rowGap={isMobileView() ? '16px' : '0'}
                justifyContent="space-between"
              >
                <GridItem>
                  <InputV2
                    type="number"
                    isDashed
                    value={suggestedOKRDetails?.startingProgressValue}
                    onChange={(event) =>
                      onChange({ ...suggestedOKRDetails, startingProgressValue: event.target.value })
                    }
                    width="200px"
                  />
                </GridItem>
                <GridItem display="flex" alignItems="center">
                  <TextBodyMedium color="#666" fontSize="12px">
                    to
                  </TextBodyMedium>{' '}
                </GridItem>
                <GridItem>
                  <InputV2
                    type="number"
                    isDashed
                    value={suggestedOKRDetails?.target}
                    onChange={(event) => onChange({ ...suggestedOKRDetails, target: event.target.value })}
                    width="200px"
                  />
                </GridItem>
                <GridItem>
                  <MeasureSelect
                    measure={suggestedOKRDetails?.measure}
                    onChange={(measure) => onChange({ ...suggestedOKRDetails, measure })}
                    hideSymbolText
                  />
                </GridItem>
                <GridItem gridColumn={isMobileView() ? '2/4' : 'auto'}>
                  <Tooltip label={suggestedOKRDetails?.measureType}>
                    <InputV2
                      type="text"
                      isDashed
                      value={suggestedOKRDetails?.measureType}
                      onChange={(event) => onChange({ ...suggestedOKRDetails, measureType: event.target.value })}
                    />
                  </Tooltip>
                </GridItem>
              </Grid>
            </Box>
          )}

          {/* <Alignment /> */}
          <Box w="100%">
            <Grid {...alignmentGridTemplate}>
              <GridItem display="flex">
                <TimelineSelect
                  selectedTimeline={suggestedOKRDetails?.timeBoundDetails}
                  showCalendarIcon
                  isReadOnly={isAlignmentDisabled}
                  onSelect={(timeBoundDetails) =>
                    onChange({
                      ...suggestedOKRDetails,
                      timeBoundDetails,
                      parentPost: {
                        uuid: null,
                        text: 'None',
                        type: OKR_TYPES.OBJECTIVE,
                        visibilityType: 'PRIVATE',
                      },
                      startTS: timeBoundDetails?.startTS,
                      dueTS: timeBoundDetails?.endTS,
                    })
                  }
                />
              </GridItem>
              <GridItem
                paddingRight="12px"
                overflow="hidden"
                sx={{
                  '.parent-okr-component': {
                    height: '44px',
                  },
                }}
              >
                <ParentOKR
                  labelText={labelText}
                  parentPost={suggestedOKRDetails?.parentPost}
                  isReadOnly={isAlignmentDisabled}
                  okrUuid={okrDetails?.uuid}
                  okrType={type}
                  okrVisibilityType={visibilityType}
                  okrText={suggestedOKRDetails?.title}
                  objectiveLevel={objectiveLevel}
                  okrHasNoChildren={!ifGoalChildExists}
                  timeBoundDetails={suggestedOKRDetails?.timeBoundDetails}
                  showLabel={false}
                  isOpenAsChild
                  leftPanelDetails={{
                    type: SECONDARY_PANEL_TYPES.TITLE_AND_METRICS_SUGGESTION_PANEL,
                    details: {
                      okrDetails: { ...okrDetails, suggestedOKRDetails: allSuggestedOKRDetails, currentCardIndex },
                      isNewOKR,
                    },
                  }}
                />
              </GridItem>
            </Grid>
          </Box>

          {showOwnersAndCollaborators && (
            <Flex
              width="100%"
              sx={{
                '.owners-collabs': {
                  width: '100%',
                },
              }}
            >
              <OKROwnerAndCollaboratorComponent
                isOwner
                ownersAndCollaborators={suggestedOKRDetails?.toIds}
                onChange={(toIds) => onChange({ ...suggestedOKRDetails, toIds })}
                showName
                showIcon
              />
            </Flex>
          )}
        </Flex>
      </Box>
    </Flex>
  );
}
export default GradientCardV2;
